import React, { useState, useEffect, useRef } from 'react';

// مخزن گلوبال برای نگهداری کانفیگ برنامه‌ها (چون فایل‌های JS قدیمی خارج از React اجرا می‌شوند)
const globalRegistry = {
    apps: {},
};

// اطمینان از وجود توابع گلوبال برای سازگاری با فایل‌های قدیمی
if (typeof window !== 'undefined') {
    // 1. تابع ثبت برنامه که توسط فایل‌های JS صدا زده می‌شود
    window.registerApp = (id, config) => {
        globalRegistry.apps[id] = config;
        // ایونت سفارشی برای اطلاع‌رسانی به کامپوننت (در صورتی که همزمان لود شوند)
        window.dispatchEvent(new CustomEvent(`app-registered-${id}`));
    };

    // 2. آبجکت appLoader برای پشتیبانی از دکمه‌های محاسبه (onclick="appLoader.runCalculation(...)")
    if (!window.appLoader) {
        window.appLoader = {
            runCalculation: (appId) => {
                const app = globalRegistry.apps[appId];
                if (app && typeof app.calculate === 'function') {
                    app.calculate();
                } else {
                    console.warn(`Calculation function not found for app: ${appId}`);
                }
            },
            // متدهای load و register دیگر نیازی نیستند اما برای جلوگیری از خطا نگه داشته می‌شوند
            load: () => {},
            register: window.registerApp
        };
    }
}

const AppLoader = ({ appId, onTitleChange }) => {
    const [status, setStatus] = useState('idle'); // idle, loading, active, error
    const [content, setContent] = useState('');
    const containerRef = useRef(null);
    const loadedScripts = useRef(new Set());

    // اثر جانبی برای بارگذاری فایل برنامه وقتی appId تغییر می‌کند
    useEffect(() => {
        if (!appId) {
            setStatus('idle');
            setContent('');
            if (onTitleChange) onTitleChange('انتخاب برنامه');
            return;
        }

        const loadAppScript = async () => {
            // اگر قبلاً ثبت شده، سریع رندر کن
            if (globalRegistry.apps[appId]) {
                renderApp(appId);
                return;
            }

            setStatus('loading');
            
            // جلوگیری از لود تکراری اسکریپت
            if (!loadedScripts.current.has(appId) && !document.querySelector(`script[src*="MyCode/${appId}.js"]`)) {
                try {
                    await new Promise((resolve, reject) => {
                        const script = document.createElement('script');
                        script.src = `MyCode/${appId}.js?v=${new Date().getTime()}`;
                        script.async = true;
                        
                        // گوش دادن به ایونت ثبت برنامه (برای اطمینان از اینکه فایل واقعا اجرا شده)
                        const handleRegister = () => {
                            window.removeEventListener(`app-registered-${appId}`, handleRegister);
                            resolve();
                        };
                        window.addEventListener(`app-registered-${appId}`, handleRegister);

                        script.onload = () => {
                            // فال‌بک: اگر ایونت فایر نشد، کمی صبر کن و ریزالو کن
                            setTimeout(() => {
                                window.removeEventListener(`app-registered-${appId}`, handleRegister);
                                resolve();
                            }, 100);
                        };
                        
                        script.onerror = () => {
                            window.removeEventListener(`app-registered-${appId}`, handleRegister);
                            reject(new Error(`Failed to load script: ${appId}`));
                        };
                        
                        document.body.appendChild(script);
                    });
                    
                    loadedScripts.current.add(appId);
                    renderApp(appId);
                } catch (err) {
                    console.error(err);
                    setStatus('error');
                }
            } else {
                // اگر اسکریپت قبلا لود شده اما هنوز در رجیستری نیست (شاید در حال لود است)
                // کمی صبر می‌کنیم
                setTimeout(() => renderApp(appId), 200);
            }
        };

        loadAppScript();
    }, [appId]);

    const renderApp = (id) => {
        const app = globalRegistry.apps[id];
        if (!app) {
            // تلاش مجدد کوتاه اگر هنوز رجیستر نشده
            setTimeout(() => {
                if (globalRegistry.apps[id]) {
                    renderApp(id);
                } else {
                    setStatus('error');
                }
            }, 500);
            return;
        }

        // دریافت HTML از متد render برنامه
        const htmlContent = app.render();
        setContent(htmlContent);
        setStatus('active');
        
        if (onTitleChange && app.title) {
            onTitleChange(app.title);
        }
    };

    // اثر جانبی برای اجرای اسکریپت‌های داخل HTML و تابع init
    useEffect(() => {
        if (status === 'active' && containerRef.current && appId) {
            const app = globalRegistry.apps[appId];
            
            // 1. اجرای اسکریپت‌های موجود در HTML (چون dangerouslySetInnerHTML اسکریپت‌ها را اجرا نمی‌کند)
            const scripts = containerRef.current.querySelectorAll('script');
            scripts.forEach(oldScript => {
                const newScript = document.createElement('script');
                Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
                newScript.appendChild(document.createTextNode(oldScript.innerHTML));
                oldScript.parentNode.replaceChild(newScript, oldScript);
            });

            // 2. اجرای تابع init برنامه (اگر وجود داشته باشد)
            if (app && typeof app.init === 'function') {
                try {
                    app.init();
                } catch (e) {
                    console.error("Error in app.init():", e);
                }
            }
        }
    }, [status, content, appId]);

    // --- RENDER STATES ---

    if (status === 'loading') {
        return (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-4 fade-in">
                <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                <div>در حال دریافت برنامه از سرور...</div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <div className="text-center p-6 border border-red-500/30 bg-red-500/10 rounded-xl text-red-400 max-w-md">
                    <div className="font-bold text-lg mb-2">خطا در بارگذاری</div>
                    <div>فایل برنامه ({appId}.js) در مسیر MyCode یافت نشد یا در اجرا دچار مشکل شد.</div>
                    <button 
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded text-sm transition-colors"
                    >
                        تلاش مجدد
                    </button>
                </div>
            </div>
        );
    }

    if (status === 'idle') {
         return (
             <div className="h-full flex flex-col items-center justify-center text-slate-500 gap-4">
                 <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                     {/* آیکون قفل به صورت SVG اینلاین برای استقلال کامپوننت */}
                     <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-600"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                 </div>
                 <h3 className="text-xl font-bold text-slate-400">برنامه‌ای انتخاب نشده است</h3>
                 <p className="max-w-md text-center text-sm">لطفا جهت استفاده از ابزارهای آنلاین، یکی از موارد را از منوی لیست انتخاب کنید.</p>
             </div>
         );
    }

    return (
        <div 
            ref={containerRef}
            className="fade-in w-full h-full"
            dangerouslySetInnerHTML={{ __html: content }} 
        />
    );
};

export default AppLoader;